import { ReactElement, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import Heading from 'components/v2/Heading'
import { Button } from 'components/v2/Button'
import { isEmpty } from 'lodash'
import { getUnprocessableErrors } from '@10xlabs/utils'
import { Formik } from 'formik'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import { Row } from 'components/v2/Row'
import { FilePreview, InputFile } from 'components/v2/InputFile'
import { ApolloError } from '@apollo/client'
import { usePushFileToS3 } from 'utils/file'
import Modal from 'components/v2/Modal'
import { IBusinessClientCreditsFormValues } from 'features/BusinessClients/types'
import { InputCurrency } from 'components/InputCurrency'
import { Label } from 'components/v2/Label'
import { getFileNameWithoutExtension } from 'utils/file'
import { schema } from './schema'

const TimelineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledFormContent = styled(FormLayout.Content)`
  height: 100%;
`

const ActionsContainer = styled.div`
  margin-top: auto;
  margin-inline: auto;
`

interface CreditsModalProps {
  setIsOpen: (value: boolean) => void
  onSubmit: ({ filePath, file, amount }: { filePath: string; file?: File; amount: string }) => void
}

export const CreditsUploadReceiptModal = ({ setIsOpen, onSubmit }: CreditsModalProps): ReactElement => {
  const initialFormValues = {
    file: '',
    filePath: '',
    amount: '',
    name: ''
  }

  const pushFileToS3 = usePushFileToS3()
  const [fileUploadLoading, setFileUploadLoading] = useState(false)
  const [error, setError] = useState<ApolloError | undefined>()

  const handleSubmit = async (formValues: IBusinessClientCreditsFormValues): Promise<void> => {
    if (!formValues.file) {
      onSubmit({
        filePath: '',
        amount: formValues.amount
      })
      setIsOpen(false)
      return
    }
    try {
      setError(undefined)
      setFileUploadLoading(true)
      const path = await pushFileToS3(formValues.file as File)
      setFileUploadLoading(false)

      if (path) {
        setIsOpen(false)
        onSubmit({
          filePath: path,
          file: formValues.file as File,
          amount: formValues.amount
        })
      }
    } catch (error) {
      // @ts-expect-error FIXME
      setError(error)
      setFileUploadLoading(false)
    }
  }
  const intl = useIntl()
  return (
    <Modal.Container onClose={(): void => setIsOpen(false)}>
      <TimelineWrapper>
        <Modal.Header>
          <Heading.H5>
            <FormattedMessage id="creditsUploadReceiptModal.uploadFile.title" defaultMessage="Mobile Kredits" />
          </Heading.H5>
        </Modal.Header>
        <Formik initialValues={initialFormValues} onSubmit={handleSubmit} validationSchema={schema(intl)}>
          {({ handleSubmit, values, handleBlur, setValues, setFieldValue, touched, errors }): ReactElement => {
            const handleFileChange = async (file: File | ''): Promise<void> => {
              if (!file) {
                setValues({ ...values, file: '', name: '', amount: values.amount })
                return
              } else {
                // @ts-expect-error FIXME
                setValues({ ...values, file: file, name: getFileNameWithoutExtension(file), amount: values.amount })
              }
            }

            return (
              <StyledFormContent as="form" onSubmit={handleSubmit}>
                <Row>
                  <Column>
                    <Label>
                      <FormattedMessage id="kredits.actions.depositKreditsModal.amount" defaultMessage="Amount" />
                    </Label>
                    <InputCurrency
                      id="amount"
                      value={values.amount}
                      placeholder="$0.00"
                      onChange={(value): void => {
                        if (value.length <= 8) {
                          setValues({ ...values, amount: value })
                        }
                      }}
                      maxLength={11}
                      onBlur={handleBlur}
                      touched={touched.amount}
                      errors={errors.amount}
                      hideSuffix
                    />
                  </Column>
                  <Column>
                    <Label>
                      <FormattedMessage id="dashboard.credits.deposit.amount" defaultMessage="RECEIPT" />
                    </Label>
                    <InputFile
                      touched={touched.file}
                      errors={errors.file}
                      onChange={handleFileChange}
                      accept={{
                        'image/jpeg': [],
                        'image/jpg': [],
                        'image/png': [],
                        'application/pdf': []
                      }}
                    />
                    {!isEmpty(values.file) && (
                      <FilePreview
                        // @ts-expect-error FIXME
                        file={values.file as File}
                        // @ts-expect-error FIXME
                        filename={(values.file as File).name}
                        onRemoveFile={(): void => {
                          setFieldValue('file', '')
                        }}
                      />
                    )}
                  </Column>
                </Row>
                <Row></Row>
                <ActionsContainer>
                  {error && (
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage
                        id="creditsUploadReceiptModal.form.error"
                        defaultMessage="There was an error uploading the file"
                      />
                    </ErrorMessage>
                  )}
                  <FormLayout.Actions>
                    <Button kind="cancel" onClick={(): void => setIsOpen(false)}>
                      <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button type="submit" kind="primary" loading={fileUploadLoading}>
                      <FormattedMessage id="dictionary.upload" defaultMessage="Upload" />
                    </Button>
                  </FormLayout.Actions>
                </ActionsContainer>
              </StyledFormContent>
            )
          }}
        </Formik>
      </TimelineWrapper>
    </Modal.Container>
  )
}
